import React, { useEffect, useRef } from "react";
import { CaretLeft, CaretRight, X as CloseIcon } from "@phosphor-icons/react";

/**
 * Full-screen media lightbox.
 * Props:
 *   items   — array of media records ({ file_id, storage_path, poster_path, kind })
 *   index   — currently open index (number). null/undefined = closed.
 *   mediaUrl(path) => full URL. Caller supplies since the base URL isn't known here.
 *   onClose()
 *   onIndexChange(nextIndex)
 * Behaviour: ← → arrows to navigate, Esc to close, tap outside media to close.
 */
export default function MediaLightbox({ items = [], index, mediaUrl, onClose, onIndexChange }) {
  const open = index != null && items[index];
  const total = items.length;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft" && total > 1) onIndexChange?.((index - 1 + total) % total);
      if (e.key === "ArrowRight" && total > 1) onIndexChange?.((index + 1) % total);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, total, onClose, onIndexChange]);

  // Touch swipe navigation. Threshold ~50px horizontal so accidental taps
  // don't trigger. Ignores mostly-vertical swipes (scroll gesture).
  const touchRef = useRef({ x: 0, y: 0, t: 0 });
  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches?.[0];
    const start = touchRef.current;
    if (!t || !start.t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const dt = Date.now() - start.t;
    // Must be primarily horizontal, > 50px, and within 800ms.
    if (dt < 800 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.4 && total > 1) {
      if (dx < 0) onIndexChange?.((index + 1) % total);       // swipe left → next
      else        onIndexChange?.((index - 1 + total) % total); // swipe right → prev
    }
    touchRef.current = { x: 0, y: 0, t: 0 };
  };

  if (!open) return null;
  const m = items[index];
  const isVideo = m.kind && m.kind.endsWith("video");
  return (
    <div
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="media-lightbox"
      className="fixed inset-0 z-[700] bg-black/90 flex items-center justify-center p-4 touch-pan-y select-none"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="media-lightbox-close"
        className="absolute top-4 right-4 w-10 h-10 border-2 border-white bg-black/60 text-white inline-flex items-center justify-center hover:bg-white hover:text-black transition-colors"
        title="Close (Esc)"
      >
        <CloseIcon size={16} weight="bold" />
      </button>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onIndexChange?.((index - 1 + total) % total); }}
            data-testid="media-lightbox-prev"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 border-2 border-white bg-black/60 text-white inline-flex items-center justify-center hover:bg-white hover:text-black"
            title="Previous (←)"
          >
            <CaretLeft size={18} weight="bold" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onIndexChange?.((index + 1) % total); }}
            data-testid="media-lightbox-next"
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 border-2 border-white bg-black/60 text-white inline-flex items-center justify-center hover:bg-white hover:text-black"
            title="Next (→)"
          >
            <CaretRight size={18} weight="bold" />
          </button>
        </>
      )}

      <div
        onClick={(e) => e.stopPropagation()}
        className="max-w-[min(96vw,1200px)] max-h-[92vh] flex flex-col items-center gap-2"
      >
        {isVideo ? (
          <video
            key={m.file_id || m.media_id}
            src={mediaUrl(m.storage_path)}
            poster={m.poster_path ? mediaUrl(m.poster_path) : undefined}
            controls
            autoPlay
            playsInline
            className="max-w-full max-h-[85vh] bg-black block"
          />
        ) : (
          <img
            key={m.file_id || m.media_id}
            src={mediaUrl(m.storage_path)}
            alt=""
            className="max-w-full max-h-[85vh] object-contain block"
          />
        )}
        {total > 1 && (
          <div className="font-mono text-[10px] uppercase tracking-wider text-white/70">
            {index + 1} / {total}
          </div>
        )}
        {m.caption && (
          <div
            className="max-w-full text-center text-white/90 text-sm px-3 py-2 bg-black/60 border border-white/20"
            data-testid="media-lightbox-caption"
          >
            {m.caption}
          </div>
        )}
      </div>
    </div>
  );
}
